// JanData.ai — Embodied Data for Physical AI

export const heroData = {
  tagline: "Embodied Datasets for Physical AI",
  headline: "The World's Most Diverse Embodied Data for Physical AI",
  subheadline: "Powering the next generation of robotics and embodied intelligence with authentic human demonstration data — captured at scale across India's diverse industrial landscape.",
  stats: [
    { value: "57M+", label: "Worker Network" },
    { value: "100K+", label: "Hours Collected" },
    { value: "100+", label: "Industries Covered" },
    { value: "2–3x", label: "Lower Cost" },
  ],
};

export const dataAdvantageData = {
  sectionTag: "THE DATA ADVANTAGE",
  title: "Purpose-Built Hardware. Unmatched Scale.",
  subtitle: "Our proprietary wearable capture platform and India's largest blue-collar workforce give us a data moat no one else can replicate.",
  hardware: {
    name: "Multi-Camera System Purpose-Built for Embodied AI Data Collection",
    tagline: "Proprietary wearable hardware designed for large-scale data capture",
    specs: [
      { label: "Cameras", value: "6 Synchronized" },
      { label: "Resolution", value: "Full-HD 1080p @ 30fps" },
      { label: "Depth", value: "Stereoscopic (Real, Not Estimated)" },
      { label: "Motion", value: "3 IMU Sensors @ 200Hz" },
      { label: "Weight", value: "~250g" },
      { label: "Storage", value: "~20hrs On-Board" },
    ],
    capabilities: [
      "Egocentric first-person video from 6 views",
      "True metric depth & 3D scene geometry",
      "Hardware-synchronized head & body motion",
      "One-button capture, USB-C powered",
      "ML-ready structured output",
    ],
  },
  networkStats: [],
};

export const useCasesData = {
  sectionTag: "USE CASES",
  title: "Training Data for Every Robotics Application",
  subtitle: "From dexterous manipulation to autonomous navigation — we capture the human demonstrations your models need.",
  cases: [
    {
      id: "manipulation",
      title: "Dexterous Manipulation",
      description: "Precision electronics assembly — PCB soldering, component placement, wire routing, and quality inspection captured from skilled technicians working on circuit boards and micro-components.",
      tags: ["Electronics", "Soldering", "Assembly", "Fine Motor"],
      image: "https://images.unsplash.com/photo-1563770660941-20978e870e26?w=600&q=80",
    },
    {
      id: "egocentric",
      title: "Egocentric Task Demonstrations",
      description: "First-person video of precise multi-step tasks — pipetting, sample preparation, centrifugation, and slide analysis — from lab technicians' exact point of view using our proprietary wearable capture system.",
      tags: ["First-Person", "Diagnostics", "Imitation Learning"],
      image: "https://images.unsplash.com/photo-1668243304131-87cb6634f85b?w=600&q=80",
    },
    {
      id: "process",
      title: "Industrial Process Capture",
      description: "End-to-end recording of complex industrial workflows — automotive assembly lines, food processing, manufacturing — with full annotation pipelines.",
      tags: ["Workflow", "Annotation", "Quality Control"],
      image: "https://images.unsplash.com/photo-1764114903065-4cc137dd303b?w=600&q=80",
    },
    {
      id: "scene-mapping",
      title: "Scene & Environment Mapping",
      description: "Multi-sensor capture of industrial environments — factories, warehouses, construction sites — for 3D reconstruction, navigation, and safety mapping.",
      tags: ["3D Mapping", "LiDAR", "Point Clouds"],
      image: "https://images.unsplash.com/photo-1630309904321-4f320230bced?w=600&q=80",
    },
  ],
};

export const videoSamplesData = {
  sectionTag: "DATA SAMPLES",
  title: "Embodied Data Samples",
  subtitle: "Real egocentric data captured from industrial settings across India using our proprietary wearable hardware.",
  videos: [
    { url: "https://www.youtube.com/embed/jVZYor4p_zA", title: "PCB Assembly" },
    { url: "https://www.youtube.com/embed/BoSkQnlnG34", title: "Sheet Metal Bending" },
    { url: "https://www.youtube.com/embed/3tpi-ek3-Yo", title: "Sheet Metal Welding" },
  ],
};

export const statsData = {
  sectionTag: "WHY JANDATA",
  title: "The India Advantage",
  subtitle: "Built on Vahan.ai — India's largest blue-collar labor marketplace. A decade of operational infrastructure uniquely positioned for physical AI data at scale.",
  stats: [
    { stat: "57M+", label: "Worker Network", description: "Massive, engaged network from India's largest labor marketplace" },
    { stat: "100+", label: "Industries Covered", description: "Manufacturing, construction, logistics, agriculture & more" },
    { stat: "100K+", label: "Hours of Data", description: "Real-world embodied data collected across diverse environments" },
    { stat: "900+", label: "Cities Covered", description: "Tier 1 to Tier 4 coverage across the country" },
    { stat: "10 Years", label: "Marketplace Operations", description: "Powered by Vahan.ai with proven operational infrastructure" },
    { stat: "2–3x", label: "Lower Cost", description: "India's cost advantage without compromising on quality" },
  ],
};

export const contactData = {
  sectionTag: "GET IN TOUCH",
  title: "Let's Build Your Dataset",
  subtitle: "Tell us about your physical AI data needs — our team will scope a custom collection plan.",
  successMessage: "Thank you! Our team will reach out shortly to discuss your dataset requirements.",
  errorMessage: "Something went wrong. Please try again or email us directly.",
};

// Dataset Catalogue Data
export const catalogueStats = [
  { value: "50M+", label: "Worker Network" },
  { value: "100K+", label: "Video Hours" },
  { value: "100+", label: "Industries Covered" },
];

// Hardware specifications for the catalogue page
export const hardwareSpecs = [
  {
    title: "6-Camera Multi-View",
    description: "Synchronized cameras give wide, near-panoramic coverage, including downward views centered on the hands and objects. Full-HD 1080p at 30fps."
  },
  {
    title: "Stereoscopic Depth",
    description: "Overlapping camera views capture the scene from several angles at once, recovering true metric depth and 3D geometry—not just flat RGB."
  },
  {
    title: "Motion Capture",
    description: "3 IMU sensors at 200Hz track precise head and hand orientation, enabling accurate pose reconstruction and motion analysis."
  }
];

export const datasetCategories = [
  "All",
  "Manipulation",
  "Industrial Process",
];

// Sub-filters per category (Humyn Labs style two-level filter)
export const subFilters = {
  "Manipulation": ["All Manipulation", "Assembly", "Electronics", "Auto", "Textiles", "Logistics", "Trades"],
  "Industrial Process": ["All Industrial", "Healthcare", "Facilities", "Woodwork", "Food", "Painting", "Agriculture", "Metal Work"],
};

export const datasets = [
  {
    id: "diagnostic-lab",
    name: "Diagnostic Lab Operations",
    category: "Industrial Process",
    subcategory: "Healthcare",
    environment: "Diagnostic Laboratory",
    description: "Sample collection, pipetting, centrifugation, slide preparation, and test processing — captured from lab technicians performing precise diagnostic workflows.",
    hours: "4,200+",
    episodes: "28,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Healthcare", "Pipetting", "Lab Work"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/z4egbet9_diag-lab-pipetting_1.36-2.10.mp4",
  },
  {
    id: "warehouse-operations",
    name: "Warehouse Operations",
    category: "Manipulation",
    subcategory: "Logistics",
    environment: "Distribution Warehouse",
    description: "Workers navigating warehouse aisles, locating items, picking from shelves, and sorting into bins — a core logistics automation use case.",
    hours: "5,600+",
    episodes: "45,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Logistics", "Picking", "Sorting"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/rbju6hbd_warehouse_0.21-0.42.mp4",
  },
  {
    id: "kitchen-cooking",
    name: "Kitchen & Food Preparation",
    category: "Industrial Process",
    subcategory: "Food",
    environment: "Commercial Kitchen",
    description: "Meal preparation in Indian commercial kitchens — chopping, stirring, plating, and multi-burner cooking captured from the chef's viewpoint.",
    hours: "3,800+",
    episodes: "32,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Food", "Cooking", "Dexterous"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/3qvsmry4_kitchen-prep_0.05-0.45.mp4",
  },
  {
    id: "pcb-assembly",
    name: "PCB Assembly",
    category: "Manipulation",
    subcategory: "Electronics",
    environment: "Electronics Factory",
    description: "Detailed first-person capture of printed circuit board assembly — component placement, soldering, and quality inspection by skilled technicians.",
    hours: "2,400+",
    episodes: "18,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Electronics", "Fine Motor", "Soldering"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/4w77176r_pcb-assembly_8.06-9.30.mp4",
  },
  {
    id: "commercial-cleaning",
    name: "Commercial Cleaning",
    category: "Industrial Process",
    subcategory: "Facilities",
    environment: "Commercial Buildings",
    description: "Floor mopping, surface wiping, vacuuming, and sanitation routines — captured from cleaning staff maintaining offices, malls, and industrial facilities.",
    hours: "2,900+",
    episodes: "20,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Cleaning", "Facilities", "Maintenance"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/eqh0rbwx_commercial-cleaning_0-0.40.mp4",
  },
  {
    id: "agricultural-harvesting",
    name: "Agricultural Harvesting",
    category: "Industrial Process",
    subcategory: "Agriculture",
    environment: "Farmland",
    description: "Crop harvesting across Indian farms — rice paddy cutting, vegetable picking, fruit collection — with diverse terrain and lighting conditions.",
    hours: "4,500+",
    episodes: "35,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Agriculture", "Outdoor", "Seasonal"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/k9psgawl_agriculture-greenhouse_1.20-1.50.mp4",
  },
  {
    id: "auto-bike-repair",
    name: "Auto, Bike & Equipment Repair",
    category: "Manipulation",
    subcategory: "Auto",
    environment: "Repair Workshop",
    description: "Disassembly, diagnosis, part replacement, and reassembly of automobiles, motorcycles, and industrial equipment — captured from mechanics in real service workshops.",
    hours: "3,200+",
    episodes: "24,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Automotive", "Repair", "Diagnostics"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/i7vvc5yk_bike-repair_3-47.mp4",
  },
  {
    id: "painting-finishing",
    name: "Painting & Surface Finishing",
    category: "Industrial Process",
    subcategory: "Painting",
    environment: "Paint Shop",
    description: "Spray painting, powder coating, and hand-finishing operations — capturing arm trajectories, spray patterns, and quality inspection routines.",
    hours: "1,500+",
    episodes: "10,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Painting", "Surface Treatment", "Spray"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/m2xkpu5e_painting_0.15-1.00.mp4",
  },
  {
    id: "packaging-sorting",
    name: "Packaging & Sorting",
    category: "Manipulation",
    subcategory: "Logistics",
    environment: "Packaging Facility",
    description: "Sorting, wrapping, boxing, and labeling operations — capturing rapid hand movements and object classification decisions.",
    hours: "4,100+",
    episodes: "38,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Packaging", "Sorting", "Speed"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/lot2m9cd_packaging_0-0.30.mp4",
  },
  {
    id: "electrical-work",
    name: "Electrical Work",
    category: "Manipulation",
    subcategory: "Trades",
    environment: "Residential & Commercial Sites",
    description: "Wiring, circuit installation, panel work, and electrical repairs — capturing precise hand movements, tool usage, and safety procedures in Indian electrical work settings.",
    hours: "2,600+",
    episodes: "18,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Electrical", "Wiring", "Installation"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/4ouiun4j_electrical_3.00-3.46.mp4",
  },
  {
    id: "plumbing",
    name: "Plumbing & Pipe Fitting",
    category: "Manipulation",
    subcategory: "Trades",
    environment: "Residential & Commercial Sites",
    description: "Pipe cutting, threading, fitting, joint sealing, and leak repair — captured from plumbers working in residential and commercial plumbing installations.",
    hours: "2,200+",
    episodes: "16,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Plumbing", "Pipe Fitting", "Trades"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/61jece2a_plumbing_8.01-8.23.mp4",
  },
  {
    id: "garment-stitching",
    name: "Garment Stitching & Tailoring",
    category: "Manipulation",
    subcategory: "Textiles",
    environment: "Garment Factory",
    description: "Sewing machine operation, hand-stitching, pattern cutting, and quality checking in India's massive garment manufacturing industry.",
    hours: "5,100+",
    episodes: "42,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Textiles", "Sewing", "Precision"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/u484yhs7_garment-stitching_0.31-0.54.mp4",
  },
  {
    id: "food-packing-retail",
    name: "Food Packing & Retail Service",
    category: "Industrial Process",
    subcategory: "Food",
    environment: "Retail & Snack Shops",
    description: "Packing, portioning, and serving food items in retail environments — capturing quick hand movements and customer-facing workflows in Indian snack shops and eateries.",
    hours: "3,600+",
    episodes: "28,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Food", "Retail", "Packing"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/27zg9hla_bakery-packing_1.30-1.56.mp4",
  },
  {
    id: "delivery-navigation",
    name: "Delivery Route Driving",
    category: "Industrial Process",
    subcategory: "Logistics",
    environment: "Urban & Suburban Roads",
    description: "Driving through Indian streets, navigating traffic, locating delivery addresses, and completing last-mile handoffs — captured from delivery riders across diverse urban conditions.",
    hours: "1,600+",
    episodes: "12,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Driving", "Navigation", "Last-Mile"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/kbvszvoa_delivery_0.28-1.10.mp4",
  },
  {
    id: "sheet-metal-bending",
    name: "Sheet Metal Bending",
    category: "Industrial Process",
    subcategory: "Metal Work",
    environment: "Metal Fabrication Workshop",
    description: "Workers operating bending machines and performing manual metal forming — capturing force application, tool handling, and material deformation.",
    hours: "1,800+",
    episodes: "12,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Metal Work", "Heavy Industry", "Tool Use"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/tra8cp66_sheet-metal-bending_2.44-3.21.mp4",
  },
  {
    id: "sheet-metal-welding",
    name: "Sheet Metal Welding",
    category: "Industrial Process",
    subcategory: "Metal Work",
    environment: "Metal Fabrication Workshop",
    description: "MIG and TIG welding operations captured from the welder's perspective — torch control, bead placement, and joint preparation.",
    hours: "3,100+",
    episodes: "22,000+",
    modalities: ["RGB Video", "Depth", "IMU"],
    tags: ["Welding", "Precision", "Heat Work"],
    sampleVideo: "https://customer-assets-jt897jd0.emergentagent.net/job_55a8b9b9-15d0-4863-a1a9-845a4c0ca70f/artifacts/ttjrk32w_sheet-metal-welding-2_8.20-9.00.mp4",
  },
];
